import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'

import './styles/fonts.css'
import './styles/tokens.css'
import './styles/base.css'
import './ui/ui.css'
import './features/features.css'
import './styles/wayfinder.css'

import { App } from './App'
import { SessionProvider } from './lib/session'
import { ToastProvider } from './ui/Feedback'
import { API_BASE, probeApi, setIdEvent } from './lib/api'
import { loadEventConfig, useDemoEvent } from './lib/event-config'

const container = document.getElementById('root')
if (!container) throw new Error('#root is missing from index.html')

/*
 * Boot: decide live-or-fixtures, then get the event. Render only when there is one.
 *
 * There is no hard-coded event to fall back on. An earlier version had one, and it was
 * the same mistake this whole change removes: an app carrying on with a name, dates and
 * a certificate rule from a copy nothing keeps in step is how the database and the
 * client came to disagree about what event 6 is called.
 *
 * So a live build that cannot reach the server renders the notice below instead of the
 * app. It is an uglier failure and an honest one — nobody reads a stale event name and
 * believes it. The same notice covers a failed health probe: fixtures used to take over
 * there, under a "Demo data" banner that read as vague to anyone whose real question
 * was "is it my internet or their server?". Fixtures now appear only in the demo build
 * (VITE_FORCE_MOCK=1), which has no server by definition and pins itself to the event
 * mock/fixtures.ts describes.
 */
function EventUnavailable() {
  return (
    <main className="page" style={{ display: 'grid', placeItems: 'center', minHeight: '100svh' }}>
      <div style={{ maxWidth: '30rem', textAlign: 'center' }}>
        <h1 className="display" style={{ fontSize: 'var(--step-2)' }}>
          Cannot reach the CPD server
        </h1>
        <p className="muted" style={{ marginTop: 'var(--space-sm)', lineHeight: 1.5 }}>
          Check your internet connection and try again. If your connection is fine, the
          server is down and the organisers have been told; nothing you have already
          scanned is lost.
        </p>
        <button
          type="button"
          className="btn btn--primary"
          style={{ marginTop: 'var(--space-lg)' }}
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    </main>
  )
}

async function boot() {
  const mode = await probeApi()
  if (mode === 'down') return false

  /* Fixtures: no server to ask, so the event is the one the fixtures describe. */
  if (mode === 'mock') {
    setIdEvent(useDemoEvent().id)
    return true
  }

  const config = await loadEventConfig(API_BASE)
  if (!config) return false
  setIdEvent(config.id)
  return true
}

void boot().then((ready) => {
  if (!ready) {
    createRoot(container).render(
      <StrictMode>
        <EventUnavailable />
      </StrictMode>,
    )
    return
  }

  createRoot(container).render(
    <StrictMode>
      <BrowserRouter>
        <SessionProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </SessionProvider>
      </BrowserRouter>
    </StrictMode>,
  )
})
